<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextArea from 'next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const categoriesInput = ref('');
const isSubmitting = ref(false);

const parseCategories = value =>
  value
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [categoryPart, reasonsPart] = line.split(':', 2);
      const category = categoryPart?.trim();
      const reasons = (reasonsPart || '')
        .split(',')
        .map(reason => reason.trim())
        .filter(Boolean);

      if (!category || reasons.length === 0) {
        return null;
      }

      return {
        category,
        reasons: Array.from(new Set(reasons)),
      };
    })
    .filter(Boolean);

const serializedCategories = computed(() =>
  parseCategories(categoriesInput.value)
);

watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};
    const categories = settings.contact_reason_categories || [];
    categoriesInput.value = categories
      .map(item => {
        const category = item.category;
        const reasons = (item.reasons || []).join(', ');
        return category && reasons ? `${category}: ${reasons}` : null;
      })
      .filter(Boolean)
      .join('\n');
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        contact_reason_categories: serializedCategories.value,
      },
      { silent: true }
    );
    useAlert(t('CONVERSATION_WORKFLOW.CONTACT_REASONS.SAVE.SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION_WORKFLOW.CONTACT_REASONS.SAVE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('CONVERSATION_WORKFLOW.CONTACT_REASONS.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('CONVERSATION_WORKFLOW.CONTACT_REASONS.DESCRIPTION') }}
      </p>
    </div>

    <div class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('CONVERSATION_WORKFLOW.CONTACT_REASONS.CATEGORIES.LABEL')"
          :help-message="
            t('CONVERSATION_WORKFLOW.CONTACT_REASONS.CATEGORIES.HELP')
          "
        >
          <TextArea
            v-model="categoriesInput"
            class="w-full"
            :placeholder="
              t('CONVERSATION_WORKFLOW.CONTACT_REASONS.CATEGORIES.PLACEHOLDER')
            "
          />
        </WithLabel>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('CONVERSATION_WORKFLOW.CONTACT_REASONS.SAVE.BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
